import React from 'react';
import AlertMessage from '../AlertMessage';
import { calculateAge } from '../components/DatePicker';
import TestPrepInfo from '../sample-collection/TestPrepInfo';
import EndoPlusCollectionSummary from '../sample-collection/EndoPlusCollection';
import { hasEndoPlusCustomInstructions } from '../sample-collection/EndoPlusCollection';

/**
 * Displays the list of all important notes for the order.
 * This includes xCom details, Test Prep information, and the Allergy Bloodspot disclaimer.
 */
export default function OrderNotes({ gearOrder, userInfo, omsRequisitions }) {
	const isPatient = userInfo?.type === 'prc';

	// SCR-8117: Temporary disclaimer for Allergy Bloodspot orders
	const allergyBloodspotOrderableItemIds = [981, 949];

	// xCom details will only show for patients. The Bloodspot disclaimer will show to everyone.
	const reqsWithNotes = gearOrder?.requisitions?.filter((req) => {
		if (allergyBloodspotOrderableItemIds.includes(req.omsOrderableItemId)) {
			return true;
		}
		let omsOrderableItem = omsRequisitions?.find(
			(omsReq) => omsReq?.orderableItem?.id === req.omsOrderableItemId
		)?.orderableItem;
		let showCustomInstructions = !req?.accessionNo && hasEndoPlusCustomInstructions(omsOrderableItem, req);

		return isPatient && (!!req.xComId || showCustomInstructions);
	});

	// Show TestPrep text to patients if any req is not accessioned and does not have a sample collection date
	const showTestPrep =
		isPatient &&
		!gearOrder.physicianInTheLoop &&
		gearOrder.requisitions?.some((req) => !req?.accessionNo && !req?.dateLastSampleCollected);

	const showReqNotes = reqsWithNotes.length > 0;

	if (!showReqNotes && !showTestPrep) {
		return null;
	}

	let title = showReqNotes ? 'Important notes about your order' : null;

	return (
		<AlertMessage type="info" additionalCssClass="mt-0">
			{title && <h4>{title}</h4>}
			{showReqNotes && (
				<ul style={{ marginBottom: showTestPrep ? '20px' : '' }}>
					{reqsWithNotes.map((req) => {
						const reqTitle = req.omsOrderableItemName;
						const showBloodspotWarning = allergyBloodspotOrderableItemIds.includes(req.omsOrderableItemId);
						let showCustomInstructions;
						const omsOrderableItem = omsRequisitions?.find(
							(omsReq) => omsReq?.orderableItem?.id === req.omsOrderableItemId
						)?.orderableItem;
						if (!req?.accessionNo) {
							showCustomInstructions = hasEndoPlusCustomInstructions(omsOrderableItem, req);
						}
						return (
							<li key={req.id} className="mt-10">
								<strong>{reqTitle}</strong>
								<br />
								{showBloodspotWarning && (
									<p>
										<i className="fa fa-fw fa-info-circle" aria-hidden="true"></i>
										<strong> Please note:</strong> The bloodspot card provided in the collection
										pack can be used for either IgG Food Antibodies or Fatty Acids testing.
									</p>
								)}
								{isPatient && (
									<>
										{!!req.xComId && (
											<>
												<RecollectionMessage
													xComId={req.xComId}
													gearOrder={gearOrder}
													gearReq={req}
												/>
											</>
										)}
										{showCustomInstructions && (
											<div>
												<EndoPlusCollectionSummary
													orderableItem={omsOrderableItem}
													gearRequisition={req}
													includeInstructions={true}
												/>
											</div>
										)}
									</>
								)}
							</li>
						);
					})}
				</ul>
			)}
			{showTestPrep && <TestPrepInfo userType={userInfo?.type} />}
		</AlertMessage>
	);
}

const RecollectionMessage = ({ xComId, gearOrder, gearReq }) => {
	let orderMessage = 'your original order';
	if (xComId && xComId !== 'Not Provided') {
		orderMessage = `original order ${xComId}`;
	}

	return (
		<>
			<div>This is a re-collection to complete {orderMessage}.</div>
			<div className="small">
				Your original specimens could not be processed by our lab.{' '}
				<ViewResults xComId={xComId} gearOrder={gearOrder} gearReq={gearReq} />
			</div>
		</>
	);
};

/**
 * Creates a link that will open the Results PDF.
 * We know the accessionNo for PITL orders so show a direct link. For non-PITL orders we don't know
 *  if the xComId is an accessionNo or QA/STAR. Link to prc report search.
 *
 * The link will not be displayed if the patient is less than 18 years old (We don't allow minors to download reports).
 *
 * @param xComId The original order for a resubmittal. This is used to find the correct accession details.
 * @param gearOrder The gear order
 * @param gearReq The gear requisition
 */
const ViewResults = ({ xComId, gearOrder, gearReq }) => {
	// Default to true, which will hide the button if we don't have patientInfo data
	let isMinor = true;
	const patientDOB = gearOrder?.patientInfo?.dateOfBirth;
	if (patientDOB) {
		const dob = new Date(gearOrder.patientInfo.dateOfBirth);
		isMinor = calculateAge(dob) < 18;
	}

	if (isMinor || !gearReq) {
		return null;
	}

	if (!gearOrder.physicianInTheLoop) {
		return (
			<>
				Review your{' '}
				<a href="/prc/reporting" target="_blank" rel="noopener noreferrer">
					results report
				</a>{' '}
				for details about what went wrong and follow all instructions when re-collecting and shipping specimens.
			</>
		);
	}

	const gearReqId = gearReq.id;
	let accessionDetail = gearReq.accessionDetails?.find((detail) => detail?.accessionNo);

	// PITL orders might have the accession details in PitlRecollectionDetails
	if (!accessionDetail) {
		accessionDetail = gearOrder.pitlRecollectionOrderSummary?.gearReqRecollectionMap?.[gearReqId]?.recollections
			?.flatMap((recollection) => recollection?.accessionDetails)
			?.find((detail) => detail?.accessionNo === xComId);
	}

	const accessionNo = accessionDetail?.accessionNo;
	const requisitionStatusId = accessionDetail?.requisitionStatusId;

	// Status 3, 4, or 9 are 'Released'
	const reportReleased = requisitionStatusId === 3 || requisitionStatusId === 4 || requisitionStatusId === 9;

	if (!gearReqId || !accessionNo || !reportReleased) {
		return null;
	}
	let pdfUrl = `${process.env.REACT_APP_GEAR_REST_URL}/gear-requisition/${gearReqId}/viewResultsPDF?dateOfBirth=${patientDOB}&accessionNo=${accessionNo}`;

	/**
	 * Opens a result PDF in a new window.
	 */
	const viewPDF = (event) => {
		event.preventDefault();
		// Call fetch() to generate a new access_token before opening the PDF window
		fetch().then(() => {
			window.open(pdfUrl, '_blank');
		});
	};

	return (
		<>
			Review your{' '}
			<button className="btn-link btn-unstyled" onClick={viewPDF}>
				results report
			</button>{' '}
			for details about what went wrong and follow all instructions when re-collecting and shipping specimens.
		</>
	);
};